/**
 * SEO Head Component
 * 
 * Dynamically injects meta tags, title, and structured data
 * @module SEO-Head-Component
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useParams } from 'react-router-dom';
import { getMetaForRoute, organizationStructuredData, type MetaData } from '../data/seo-meta';

interface SEOHeadProps {
  customMeta?: Partial<MetaData>;
  structuredData?: object | object[];
  noIndex?: boolean;
}

/**
 * SEO Head component for dynamic meta tag injection
 * @param customMeta - Optional custom meta data to override defaults
 * @param structuredData - Optional structured data for rich snippets
 * @param noIndex - Whether to prevent search engine indexing
 */
const SEOHead: React.FC<SEOHeadProps> = ({ 
  customMeta = {}, 
  structuredData, 
  noIndex = false 
}) => {
  const location = useLocation();
  const params = useParams();
  
  // Get project ID for project detail pages
  const projectId = params.projectId;
  
  // Get base meta data for current route
  const baseMeta = getMetaForRoute(location.pathname, projectId);
  
  // Merge base meta with custom overrides
  const meta: MetaData = {
    ...baseMeta,
    ...customMeta
  };

  // Ensure absolute URLs for Open Graph
  const absoluteOgImage = meta.ogImage?.startsWith('http') 
    ? meta.ogImage 
    : `https://theanathoth.com${meta.ogImage}`;

  const absoluteCanonical = meta.canonicalUrl?.startsWith('http')
    ? meta.canonicalUrl
    : `https://theanathoth.com${meta.canonicalUrl || location.pathname}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      {meta.keywords && <meta name="keywords" content={meta.keywords} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={absoluteCanonical} />
      
      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={meta.ogType || 'website'} />
      <meta property="og:title" content={meta.ogTitle || meta.title} />
      <meta property="og:description" content={meta.ogDescription || meta.description} />
      <meta property="og:url" content={absoluteCanonical} />
      <meta property="og:site_name" content="Anathoth Technologies" />
      <meta property="og:locale" content="en_US" />
      {absoluteOgImage && <meta property="og:image" content={absoluteOgImage} />}
      {absoluteOgImage && <meta property="og:image:alt" content={meta.title} />}
      {absoluteOgImage && <meta property="og:image:width" content="1200" />}
      {absoluteOgImage && <meta property="og:image:height" content="630" />}
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.ogTitle || meta.title} />
      <meta name="twitter:description" content={meta.ogDescription || meta.description} />
      {absoluteOgImage && <meta name="twitter:image" content={absoluteOgImage} />}
      <meta name="twitter:site" content="@anathothtech" />
      <meta name="twitter:creator" content="@anathothtech" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content="Anathoth Technologies Ltd" />
      <meta name="publisher" content="Anathoth Technologies Ltd" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      
      {/* Geo Meta Tags for Local SEO */}
      <meta name="geo.region" content="UG" />
      <meta name="geo.country" content="Uganda" />
      <meta name="ICBM" content="0.3476,32.5825" />
      
      {/* Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationStructuredData)}
      </script>
      
      {/* Custom Structured Data */}
      {structuredData && (
        Array.isArray(structuredData) ? 
          structuredData.map((data, index) => (
            <script key={index} type="application/ld+json">
              {JSON.stringify(data)}
            </script>
          )) :
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
      )}
    </Helmet>
  );
};

export default SEOHead; 